import React, { useCallback, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Handle, Position } from 'reactflow';
import { Bot, Play, CheckCircle, XCircle, Clock, Database, Trash2, Lightbulb, Edit3, GitBranch, Sparkles, RefreshCw, Lock, MapPin, MessageCircle } from 'lucide-react';
import { NodeData, getStepDisplayName, getIntentDisplayText } from '../../../types';
import { useProjectStore, useFlowStore } from '../../../store';
import ModelDetailsModal from '../../models/ModelDetailsModal';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import { NodeLabel, BadgeContainer, BadgeConfig, ToolIndicator, ToolModal } from './shared';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';
import { useModels } from '../../../hooks/queries/useModels';

interface AgentNodeProps {
  data: NodeData;
  selected: boolean;
}

const AgentNode: React.FC<AgentNodeProps> = ({ data, selected }) => {
  // AC-1412 / AC-1377 P5.1: project models flow through React Query
  // (`useModels`) instead of `useProjectStore.models`. The currentProject
  // selection still lives in the project store; it's the only project-store
  // field this node touches.
  const projectId = useProjectStore((state) => state.currentProject?.project_id);
  const modelsQuery = useModels(projectId);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const { setSelectedNode, selectedNode, isModelModalOpen, setIsModelModalOpen, nodes, edges, setNodes, setEdges } = useFlowStore();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [showToolModal, setShowToolModal] = useState(false);

  // AC-1406: Delete via React Query mutation. The hook's `onSuccess`
  // invalidates `workflowSteps(id)` AND `workflowExecution(id)` per TD
  // section 2.6, replacing the legacy `apiService.deleteWorkflowStep` +
  // manual `loadWorkflow(id)` reload pattern. We still pop the deleted
  // node out of the local React Flow `nodes` array immediately so the
  // canvas reflects the removal without waiting for the steps refetch
  // to land — the seed effect rebuilds from cache once the refetch
  // completes.
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');

  // Check if node can be deleted (not referenced by other nodes)
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      n =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id)
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id]);

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;

    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });

      // Remove the node and its connected edges from local canvas state
      // for an immediate UX response. The mutation hook's invalidation
      // will trigger a refetch + seed-effect rebuild that reaches the
      // same end state.
      setNodes(nodes.filter(node => node.id !== data.id));
      setEdges(edges.filter(edge => edge.source !== data.id && edge.target !== data.id));

      setSelectedNode(null);
    } catch (error) {
      console.error('Failed to delete step:', error);
      // Show error message to user
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Play className="w-4 h-4 text-primary-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return 'from-blue-100 to-blue-50 dark:from-blue-900/80 dark:to-blue-950/70 border-blue-500';
      case 'completed':
        return 'from-green-100 to-green-50 dark:from-green-900/80 dark:to-green-950/70 border-green-500';
      case 'failed':
        return 'from-red-100 to-red-50 dark:from-red-900/80 dark:to-red-950/70 border-red-500';
      case 'pending':
        return 'from-yellow-100 to-yellow-50 dark:from-yellow-900/80 dark:to-yellow-950/70 border-yellow-500';
      default:
        return 'from-gray-100 to-white dark:from-gray-800 dark:to-cap-dark border-gray-300 dark:border-gray-600';
    }
  };

  const getAgentTypeGradient = () => {
    // Use intent-based coloring for run_agent steps
    if (data.step?.step_type === 'run_agent') {
      switch (data.step?.intent) {
        case 'create':
          return 'bg-gradient-to-r from-blue-500 to-blue-600';
        case 'enhance':
          return 'bg-gradient-to-r from-purple-500 to-purple-600';
        case 'validate':
          return 'bg-gradient-to-r from-green-500 to-green-600';
        case 'conditional':
          return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
        case 'assistant':
          return 'bg-gradient-to-r from-indigo-500 to-indigo-600';
        default:
          return 'bg-gradient-to-r from-indigo-500 to-indigo-600'; // Default RUN_AGENT color
      }
    }

    return 'bg-gradient-to-r from-gray-500 to-gray-600';
  };

  // PREFERRED: Intent-based icons provide clear action context
  const getIntentIcon = (intent: string | undefined) => {
    switch (intent) {
      case 'create':
        return <Lightbulb className="w-4 h-4" />; // Creative generation
      case 'enhance':
        return <Edit3 className="w-4 h-4" />;     // Content improvement
      case 'validate':
        return <CheckCircle className="w-4 h-4" />; // Quality verification
      case 'conditional':
        return <GitBranch className="w-4 h-4" />; // Logic branching
      case 'assistant':
        return <MessageCircle className="w-4 h-4" />; // User interaction
      default:
        return null;
    }
  };

  // Icon selection with Intent-Based priority
  const getAgentIcon = () => {
    // Priority 1: Intent-based icon (PREFERRED APPROACH)
    if (data.step?.intent) {
      return getIntentIcon(data.step.intent);
    }

    // Priority 2: Default bot icon (fallback)
    return <Bot className="w-4 h-4" />;
  };

  const formatStepTypeDisplay = () => {
    if (data.step) {
      return getStepDisplayName(data.step);
    }
    return 'Agent';
  };

  const handleModelBadgeClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Set selectedNode to this node's ID so only THIS node renders the modal
    setSelectedNode(data.step?.orchestration_step_id || null);
    setIsModelModalOpen(true);
  }, [setSelectedNode, setIsModelModalOpen, data.step?.orchestration_step_id]);

  const handleCloseModelDetails = useCallback(() => {
    setIsModelModalOpen(false);
  }, [setIsModelModalOpen]);

  const outputModel = data.agent?.output_model_id
    ? models.find(m => m.model_id === data.agent?.output_model_id) || data.agent?.output_model
    : undefined;

  // Check for parallel execution mode
  const isParallel = data.step?.execution_mode && data.step.execution_mode !== 'synchronous';

  // Session configuration
  const sessionConfig = data.step?.session_config?.enabled ? data.step.session_config : null;
  const getSessionIcon = () => {
    switch (sessionConfig?.mode) {
      case 'shared':
        return <RefreshCw className="w-3 h-3" />;
      case 'isolated':
        return <Lock className="w-3 h-3" />;
      case 'run':
        return <MapPin className="w-3 h-3" />;
      default:
        return null;
    }
  };
  const getSessionLabel = () => {
    switch (sessionConfig?.mode) {
      case 'shared':
        return 'Shared';
      case 'isolated':
        return 'Isolated';
      case 'run':
        return 'Run';
      default:
        return '';
    }
  };
  const getSessionColorScheme = () => {
    switch (sessionConfig?.mode) {
      case 'shared':
        return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-blue-700';
      case 'isolated':
        return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700';
      case 'run':
        return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700';
      default:
        return '';
    }
  };

  // Build badges array for BadgeContainer
  const badges = useMemo<BadgeConfig[]>(() => {
    const result: BadgeConfig[] = [];

    // Model badge (priority 1 - most important for data flow)
    if (outputModel) {
      result.push({
        id: 'model',
        icon: <Database className="w-3 h-3" />,
        label: outputModel.class_name || 'Model',
        variant: 'model',
        priority: 1,
        onClick: handleModelBadgeClick,
        tooltip: 'Click to view model details',
      });
    }

    // Session badge (priority 2)
    if (sessionConfig) {
      result.push({
        id: 'session',
        icon: getSessionIcon(),
        label: `Session: ${getSessionLabel()}`,
        colorScheme: getSessionColorScheme(),
        priority: 2,
      });
    }

    // Parallel badge (priority 3)
    if (isParallel) {
      result.push({
        id: 'parallel',
        icon: <Play className="w-3 h-3" />,
        label: data.step?.fan_out ? 'Parallel Fan-out' : 'Parallel',
        variant: 'parallel',
        priority: 3,
      });
    }

    return result;
  }, [outputModel, sessionConfig, isParallel, data.step?.fan_out, handleModelBadgeClick]);

  return (
    <div className="relative">
      <div
        className={`relative w-72 h-32 px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {/* Delete button - only show if node is deletable */}
        {isDeletable && (
          <button
              title="Delete step"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        )}

        {/* Target handles for dynamic edge connections */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />

        {/* Primary Intent Badge - Clean and Focused */}
        {data.agent && (
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full ${getAgentTypeGradient()} text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20`}>
            <div className="flex items-center gap-2">
              {getAgentIcon()}
              <span className="capitalize">
                {data.step?.intent ? 
                  getIntentDisplayText(data.step.intent) : 
                  formatStepTypeDisplay()}
              </span>
              {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
                <Tooltip content="Feedback Loop Hints configured">
                  <div>
                    <Sparkles className="w-3 h-3" />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        {/* Legacy intent badge removed - now integrated into main badge */}

        {/* Main Card Content */}
        <div className="flex flex-col items-center py-2">
          <div className="flex items-center gap-2 mb-1">
            <div className="p-2 rounded-full bg-white dark:bg-cap-dark shadow-sm">
              <Bot className="w-5 h-5 text-primary-600" />
            </div>
            <NodeLabel
              label={data.agent?.name || 'Unnamed Agent'}
              maxLength={18}
              fontWeight="medium"
              textColor="text-gray-900 dark:text-warm-white"
            />
            {/* Tool Indicator */}
            {data.agent && Array.isArray(data.agent.tools) && data.agent.tools.length > 0 && (
              <ToolIndicator
                tools={data.agent.tools}
                onClick={(e) => { e.stopPropagation(); setShowToolModal(true); }}
              />
            )}
            {getStatusIcon()}
          </div>

          {/* Feature Badges - uses BadgeContainer with overflow handling */}
          {badges.length > 0 && (
            <BadgeContainer
              badges={badges}
              maxVisible={2}
              layout="horizontal"
              gap="tight"
              className="mt-1"
            />
          )}
        </div>

        {data.status === 'running' && (
          <div className="absolute inset-0 rounded-lg border-2 border-blue-500 animate-pulse" />
        )}

        {/* Source handles for dynamic outgoing connections */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        
        {/* Dedicated feedback loop handles - positioned to avoid conflicts */}
        <Handle
          type="source"
          position={Position.Top}
          id="feedback-out-top"
          style={{ top: -12, left: '30%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="feedback-out-left"
          style={{ left: -8, top: '30%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Top}
          id="feedback-in-top"
          style={{ top: -12, left: '70%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="feedback-in-right"
          style={{ right: -8, top: '30%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />

        {/* Additional feedback handles for assistant loops */}
        <Handle
          type="source"
          position={Position.Right}
          id="feedback-out-right"
          style={{ right: -8, top: '70%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="feedback-out-bottom"
          style={{ left: '30%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="feedback-in-left"
          style={{ left: -8, top: '70%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="feedback-in-bottom"
          style={{ left: '70%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Model Details Popup rendered via portal - only render if THIS node opened the modal */}
      {isModelModalOpen && outputModel && selectedNode === data.step?.orchestration_step_id && ReactDOM.createPortal(
        <ModelDetailsModal
          model={outputModel}
          allModels={models}
          onClose={handleCloseModelDetails}
          onEdit={() => {}}
        />,
        document.getElementById('modal-root') as HTMLElement
      )}

      {/* Tool Modal - AC-637, AC-638, AC-639, AC-640 */}
      {showToolModal && data.agent && Array.isArray(data.agent.tools) && data.agent.tools.length > 0 && (
        <ToolModal
          tools={data.agent.tools}
          agentName={data.agent.name || 'Unnamed Agent'}
          onClose={() => setShowToolModal(false)}
          models={models}
        />
      )}
    </div>
  );
};

export default AgentNode;